import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatAreaProps, ChatMessage } from "../../../../views/crm-pages/conversation/types";
import { ExtendedTemplate } from "../../../../views/marketing-pages/create-campaign/types";
import { TicketProps } from "../../../../views/crm-pages/ticket/types";
import useRequest from "../../../hook/use-req";
import useCrmApi from "../../../hook/crm-req";
import useCrmFormData from "../../../hook/crm-formdata-req";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import {
  generateIdempotencyKey,
  getAssignedTo,
  getContactDisplayName,
  getRealtimeConversationId,
  Icons,
  isInboundDirection,
  isSameConversationId,
  renderTemplateWithVariableValues,
  restoreTemplatePlaceholders,
} from "../../../../views/crm-pages/conversation/constant";
import { useWebSocketContext, Message as WSMessage } from "../../socket-context";
import { formatDate, getColorFromId, getInitials, getStatusBadge } from "../../../../utils/function";
import { Avatar, MessageComponent } from "./avatar";
import { RaiseTicketModal } from "../../customer/ticket-pop-up";
import { ContactModal } from "../../customer/update-customer";
import { Archive, Bot, Contact, Headset, Reply, Square, Ticket, UserPlus, X } from "lucide-react";
import { TemplatePickerModal } from "./template-picker";
import { TemplateVariableModal } from "./template-variable";
import { RcsTemplatePickerModal, RcsTemplate } from "./rcs-template-picker";
import { RcsTemplateVariableModal } from "./rcs-template-variable";
import { RcsComponent } from "../../../marketing/templates/rcs/rcs-types";
import SendQuickReplyModal from "../../../quick-replies/send-modal";
import { QuickReply } from "../../../quick-replies/types";
import { AssignConversationAgentModal } from "./assign-conversation-agent-modal";
import { TeamDetails } from "../../customer/types";

type MediaType = "image" | "video" | "audio" | "document";

interface PendingAttachment {
  file: File;
  localUrl: string;
  mediaType: MediaType;
  uploading: boolean;
  fileId?: string;
  error?: boolean;
}

// ─── 24-Hour Window Banner ────────────────────────────────────────────────────

interface WindowBannerProps {
  onSelectTemplate: () => void;
}



// Possible backend flags for "a human has this chat" — returns undefined when
// the conversation doesn't carry any of them.
const readHumanControl = (conv: any): boolean | undefined => {
  if (!conv) return undefined;
  for (const key of ["human_takeover", "is_human_takeover", "takeover", "manual_reply", "bot_paused"]) {
    if (typeof conv[key] === "boolean") return conv[key];
  }
  for (const key of ["bot_active", "is_bot_active", "bot_enabled"]) {
    if (typeof conv[key] === "boolean") return !conv[key];
  }
  const mode = conv.handled_by ?? conv.control_mode ?? conv.reply_mode ?? conv.handler;
  if (typeof mode === "string" && mode) return /human|agent|manual/i.test(mode);
  return undefined;
};

// Swaps the optimistic temp message for the send response. The websocket
// often delivers the real message first; when a message with the response's
// id is already in the list, merge into it and drop the temp instead of
// leaving two copies. `local_pending` stays set until the websocket copy
// arrives (see isOwnPendingSend) — the send response's id can differ from the
// websocket message's, so ids alone can't pair them. Its `type` is kept so a
// template stays a template whatever the response says.
const settleOptimistic = (prev: ChatMessage[], tempId: string, patch: any): ChatMessage[] => {
  const realId = patch?.id;
  if (realId && prev.some(m => m.id === realId && m.id !== tempId)) {
    return prev
      .filter(m => m.id !== tempId)
      .map(m => (m.id === realId ? { ...m, ...patch, type: m.type, status: m.status || patch.status } : m));
  }
  return prev.map(m => (m.id === tempId ? { ...m, ...patch, type: m.type, local_pending: true } as ChatMessage : m));
};

const isTemplateLike = (m: any) =>
  m?.type === "template" || /^\s*template\s*:/i.test(m?.body || "") || !!m?.message_metadata?.template;

// Does this websocket message echo something we just sent from this tab?
// Text is matched by body; templates by kind, since the websocket body is
// `template:<name>` while ours is the rendered text.
const isOwnPendingSend = (m: any, incoming: any) =>
  !!m.local_pending &&
  incoming.direction === "outbound" &&
  Math.abs(new Date(m.created_at).getTime() - new Date(incoming.created_at || Date.now()).getTime()) < 60000 &&
  (m.body === incoming.body || (m.type === "template" && isTemplateLike(incoming)));

const WindowClosedBanner: React.FC<WindowBannerProps> = ({ onSelectTemplate }) => (
  <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800">
    {/* 24-hour icon */}
    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="18" r="16" stroke="#ef4444" strokeWidth="2" fill="none" />
        <text x="18" y="22" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#ef4444">24</text>
        {/* Circular arrow */}
        <path d="M18 4 A14 14 0 0 1 32 18" stroke="#ef4444" strokeWidth="2" fill="none"
          strokeLinecap="round" markerEnd="url(#arrowhead)" />
      </svg>
    </div>
    <p className="flex-1 text-xs text-slate-600 dark:text-gray-400 leading-relaxed">
      This customer is outside the 24-hour reply window. Use a template to continue the conversation.
    </p>
    <button
      onClick={onSelectTemplate}
      className="flex-shrink-0 px-4 py-2 bg-white dark:bg-gray-800 border border-slate-300 dark:border-gray-600 rounded-lg text-sm font-semibold text-slate-700 dark:text-gray-200 hover:bg-slate-50 hover:border-slate-400 dark:hover:bg-gray-700 dark:hover:border-gray-500 transition-colors"
    >
      Select template
    </button>
  </div>
);

// ─── Main ChatArea ────────────────────────────────────────────────────────────

export const ChatArea: React.FC<ChatAreaProps> = ({
  conv,
  onOpenInsights,
  onOpenForm,
  isMobile,
  onConversationUpdate,
  leadContact,
  assignedTicket,
  team = [],
  onContactUpdated,
  onTicketSaved,
  activeChannel,
  initialRecipient,
}) => {
  // Detect RCS conversation - either by rcs_account_id or activeChannel === 4 (RCS index)
  const isRcsConversation = !!(conv?.rcs_account_id) || activeChannel === 4;
  // Website chat widget (widget-integration.md): text-only replies, no
  // templates/attachments/24h window. Index 7 = "Agents" tab.
  const isWidgetConversation = (conv as any)?.channel === "widget" || (conv as any)?.channel_type === "widget" || activeChannel === 7;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem("authToken");
  const [backendTemplates, setBackendTemplates] = useState<ExtendedTemplate[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openAction, setOpenAction] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // The "…" actions menu (Update Contact / Assign Tickets / Archive /
  // Assign Agent) used to only close via its own toggle button — clicking
  // an option, or clicking anywhere else on the page, left it open behind
  // whatever modal that option opened.
  useEffect(() => {
    if (!openAction) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setOpenAction(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openAction]);

  const [existingTicket, setExistingTicket] = useState<TicketProps | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ExtendedTemplate | null>(null);
  const [templateDraft, setTemplateDraft] = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showQuickReplyModal, setShowQuickReplyModal] = useState(false);
  // No dedicated "assign conversation to agent" endpoint exists yet — this
  // is local/optimistic only until that API is confirmed (see
  // handleAssignConversationAgent below).
  const [showAssignAgentModal, setShowAssignAgentModal] = useState(false);
  const [assignedAgentId, setAssignedAgentId] = useState<string | null>(null);

  // Variable-configuration popup state — shown right after a template is
  // picked from TemplatePickerModal, before it becomes the active
  // selectedTemplate / templateDraft.
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<ExtendedTemplate | null>(null);
  // Ordered values for template_attributes.body, captured from the variable modal.
  const [templateVarValues, setTemplateVarValues] = useState<string[]>([]);

  // RCS template state — RCS templates are a separate catalog from WhatsApp's
  // and are sent via the direct /messages/send endpoint (type: "template",
  // provider_payload), not /messages/send-template (WhatsApp-only per rcs.md).
  const [showRcsTemplatePicker, setShowRcsTemplatePicker] = useState(false);
  const [showRcsVariableModal, setShowRcsVariableModal] = useState(false);
  const [selectedRcsTemplate, setSelectedRcsTemplate] = useState<RcsTemplate | null>(null);
  const [pendingRcsTemplate, setPendingRcsTemplate] = useState<RcsTemplate | null>(null);
  const [rcsContentVariables, setRcsContentVariables] = useState<Record<string, string>>({});

  // New conversation flow state
  const [, setNewConvStep] = useState<"recipient" | "template">("recipient");
  const [newConvRecipient, setNewConvRecipient] = useState("");

  // A deep-link (broadcast report, Leads, etc.) found no existing
  // conversation for this phone number and fell back to a fresh compose —
  // prefill the recipient so the user doesn't have to re-type it.
  useEffect(() => {
    if (!conv && initialRecipient) {
      setNewConvRecipient(initialRecipient);
    }
  }, [conv, initialRecipient]);

  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [manualReplyEnabled, setManualReplyEnabled] = useState(false);
  const [takeoverLoading, setTakeoverLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // A file picked but not sent yet — shown as a preview in the composer so a
  // caption can be added before it actually goes out.
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesCache = useRef<Map<string, ChatMessage[]>>(new Map());
  const isInitialLoad = useRef<boolean>(true);
  const convIdRef = useRef<string | null>(null);

  const { makeRequest: getTemplates } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });
  // Per rcs.md: POST /api/v1/files/upload (CRM base, multipart field "file")
  // returns { file_id, status, object_key, mime_type, size_bytes } directly —
  // no uploaded_files[] wrapper, and file_id is a real backend-issued id.
  const { makeRequest: uploadFile } = useCrmFormData("/files/upload", "POST", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getMessagesRequest } = useCrmApi(
    `/conversations/${conv?.id}/messages`, "GET", { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: sendMessageRequest } = useCrmApi(
    `/messages/send`, "POST", { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: sendTemplateMessageRequest } = useCrmApi(
    `/messages/send-template`, "POST", { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: createConversationRequest } = useCrmApi(
    `/conversations`, "POST", { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: getTicketWithConversation } = useCrmApi(
    `/tickets?conversation_id=${currentConversationId}`, "GET", { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: takeoverRequest } = useCrmApi(
    `/conversations/${conv?.id}/takeover`, "POST", { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: handoffToBotRequest } = useCrmApi(
    `/conversations/${conv?.id}/handoff-to-bot`, "POST", { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: archiveConversationRequest } = useCrmApi(
    `/conversations/${conv?.id}/archive`, "PATCH", { Authorization: `Bearer ${token}` }
  );


  const getTemplateBodyValues = (
    template: ExtendedTemplate,
    draft?: string
  ): string[] => {
    const vars = [...(template.variables ?? [])]
      .sort((a, b) => a.index - b.index);

    if (!vars.length) return [];
    return vars.map(v => v.example ?? "");
  };

  const convertToFrontendTemplate = (backendTemplate: TemplateModule): ExtendedTemplate => {
    const type: "Text" | "Media + Text" = backendTemplate.header_type === "image" ? "Media + Text" : "Text";
    const originalContent = restoreTemplatePlaceholders(backendTemplate.content, backendTemplate.variables || []);
    const preview = originalContent.replace(/{{(\d+)}}/g, (match, index) => {
      const variable = backendTemplate.variables?.find(v => v.index === parseInt(index));
      return variable?.example || match;
    });
    return {
      id: backendTemplate.id,
      name: backendTemplate.template_name,
      type,
      preview,
      originalContent,
      variables: backendTemplate.variables || [],
      header_type: backendTemplate.header_type || null,
      header_content: backendTemplate.header_content || null,
      footer: backendTemplate.footer || null,
      buttons: backendTemplate.buttons || null,
      language: backendTemplate.language,
      category: backendTemplate.category,
      wa_status: backendTemplate.wa_status,
      waba_id: backendTemplate.waba_id,
      // preserve snapshot_url from the backend template
      snapshot_url: (backendTemplate as any).snapshot_url || null,
    } as ExtendedTemplate & { snapshot_url?: string | null };
  };

  const { subscribe, unsubscribe } = useWebSocketContext();

  useEffect(() => { convIdRef.current = conv?.id ?? null; }, [conv?.id]);

  useEffect(() => {
    if (!conv?.id) return;
    subscribe(conv.id);
    return () => { unsubscribe(conv.id); };
  }, [conv?.id, subscribe, unsubscribe]);

  const fetchTemplates = async () => {
    const [response] = await getTemplates(undefined, { channel: "WhatsApp" });
    const raw = response?.data ?? (Array.isArray(response) ? response : []);
    const approved = raw.filter((t: TemplateModule) => t?.wa_status === "APPROVED");
    setBackendTemplates(approved.map(convertToFrontendTemplate));
  };

  useEffect(() => { fetchTemplates(); }, []);

  // Catalog for resolving sent template messages in the thread (placeholders kept).
  const templateCatalog = React.useMemo(
    () => backendTemplates.map(t => ({ name: t.name, language: t.language, content: t.originalContent, buttons: t.buttons })),
    [backendTemplates]
  );

  useEffect(() => {
    const fetchTicket = async () => {
      if (!currentConversationId) return;
      try {
        const [response] = await getTicketWithConversation();
        setExistingTicket(Array.isArray(response) && response.length > 0 ? response[0] : null);
      } catch { setExistingTicket(null); }
    };
    fetchTicket();
  }, [currentConversationId]);

  useEffect(() => { if (assignedTicket !== undefined) setExistingTicket(assignedTicket); }, [assignedTicket]);
  useEffect(() => { setCurrentConversationId(conv?.id ?? null); }, [conv]);
  // Who's replying: read it from the conversation when the backend sends a
  // control flag (the exact field isn't pinned down yet, so a few likely
  // names are checked); otherwise infer from the thread — the latest
  // outbound message coming from a human means a human has the chat.
  const conversationControl = readHumanControl(conv);
  const inferredControl = React.useMemo(() => {
    const lastOut = [...messages].reverse().find(m => m.direction === "outbound" && m.sender_type);
    return lastOut ? lastOut.sender_type === "human" : undefined;
  }, [messages]);
  const userSetControl = useRef(false);
  useEffect(() => {
    userSetControl.current = false;
    setManualReplyEnabled(conversationControl ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv?.id, conversationControl]);
  useEffect(() => {
    if (conversationControl === undefined && inferredControl !== undefined && !userSetControl.current) {
      setManualReplyEnabled(inferredControl);
    }
  }, [conversationControl, inferredControl]);

  // ── Manual reply takeover toggle ────────────────────────────────────────────
  const handleToggleManualReply = async () => {
    if (!conv?.id || takeoverLoading) return;
    const next = !manualReplyEnabled;
    setTakeoverLoading(true);
    try {
      const [, status] = next ? await takeoverRequest() : await handoffToBotRequest();
      if (status >= 200 && status < 300) {
        userSetControl.current = true;
        setManualReplyEnabled(next);
        toast.success(next ? "You've taken over this conversation" : "Handed back to the bot");
      } else {
        toast.error(next ? "Failed to take over conversation" : "Failed to hand off to bot");
      }
    } catch (error) {
      console.error("Error toggling manual reply:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setTakeoverLoading(false);
    }
  };

  // ── Archive conversation ────────────────────────────────────────────────────
  // ChatArea doesn't own the conversation list, so notify whoever does via a
  // window event — same bridging pattern used for cross-page conversation
  // selection (see "navigate-to-conversation" in crm-pages/conversation/index.tsx).
  const handleArchiveConversation = async () => {
    if (!conv?.id || archiving) return;
    setArchiving(true);
    try {
      const [, status] = await archiveConversationRequest();
      if (status >= 200 && status < 300) {
        toast.success("Conversation archived");
        window.dispatchEvent(new CustomEvent("conversation-archived", { detail: { conversationId: conv.id } }));
      } else {
        toast.error("Failed to archive conversation");
      }
    } catch (error) {
      console.error("Error archiving conversation:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setArchiving(false);
      setOpenAction(false);
    }
  };

  // ── Assign conversation to an agent ─────────────────────────────────────────
  // No backend endpoint for this exists yet (confirmed 2026-08-21) — this is
  // the same Assign Agent UI/flow as the Leads dashboard
  // (pages/leads/assign-agent-modal.tsx), wired to local state only for now.
  // TODO: once the real "assign conversation" API is confirmed, replace the
  // optimistic setAssignedAgentId below with the actual request (and likely
  // move assignedAgentId to come from the conversation record itself instead
  // of local state).
  const handleAssignConversationAgent = async (member: TeamDetails | null) => {
    setAssignedAgentId(member?.id ?? null);
    toast.success(member ? `Assigned to ${member.first_name} ${member.last_name}` : "Unassigned");
    setOpenAction(false);
  };
  const assignedAgent = team.find((m) => m.id === assignedAgentId) || null;

  const fetchMessages = useCallback(async () => {
    if (!conv?.id) return;
    const cached = messagesCache.current.get(conv.id);
    if (cached) { setMessages(cached); return; }
    try {
      const [response] = await getMessagesRequest();
      let arr: ChatMessage[] = [];
      if (Array.isArray(response)) arr = response;
      else if (Array.isArray(response?.data)) arr = response.data;
      else if (Array.isArray(response?.messages)) arr = response.messages;
      arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      messagesCache.current.set(conv.id, arr);
      setMessages(arr);
    } catch (error) { console.error("Error fetching messages:", error); }
  }, [conv?.id]);

  useEffect(() => {
    if (conv?.id) { isInitialLoad.current = true; fetchMessages(); }
    else setMessages([]);
  }, [conv?.id]);

  useEffect(() => {
    if (!bottomRef.current) return;
    if (isInitialLoad.current) {
      bottomRef.current.scrollIntoView({ behavior: "auto" });
      isInitialLoad.current = false;
    } else {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  // WebSocket handlers
  useEffect(() => {
    const cleanBody = (body: string) => {
      let b = body || "";
      // A body that is only `template:<name>` is kept — MessageComponent
      // needs the name to resolve the real template text.
      if (/^\s*template\s*:\s*\S+\s*$/i.test(b)) return b.trim();
      b = b.replace(/^template:\s*\w+\s*$/gim, "").trim();
      b = b.replace(/\ntemplate:\s*\w+\s*$/gim, "").trim();
      b = b.replace(/template:last/gi, "").trim();
      return b;
    };

    const handleWSMessageCreated = (event: CustomEvent<{ message: WSMessage; conversationId: string }>) => {
      const { message } = event.detail;
      const conversationId = getRealtimeConversationId(message as any, event.detail.conversationId);
      const cleaned = { ...message, body: cleanBody(message.body) };

      if (isSameConversationId(convIdRef.current, conversationId)) {
        setMessages(prev => {
          const exists = prev.some(m =>
            m.id === cleaned.id ||
            (m.id.startsWith("temp-") && m.body === cleaned.body &&
              Math.abs(new Date(m.created_at).getTime() - new Date(cleaned.created_at).getTime()) < 5000)
          );
          // Our own send echoed back (oldest pending first, so back-to-back
          // sends pair in order) — replace the local copy, keeping its
          // rendered template text over a bare `template:<name>` body.
          const pendingIdx = exists ? -1 : prev.findIndex(m => isOwnPendingSend(m, cleaned));
          let next: ChatMessage[];
          if (pendingIdx !== -1) {
            next = prev.map((m, i) => {
              if (i !== pendingIdx) return m;
              const body = /^\s*template\s*:/i.test(cleaned.body || "") ? m.body : cleaned.body;
              const { local_pending, ...rest } = { ...m, ...cleaned, body } as any;
              return rest as ChatMessage;
            });
          } else if (exists) {
            next = prev.map(m =>
              m.id === cleaned.id || (m.id.startsWith("temp-") && m.body === cleaned.body)
                ? { ...m, ...cleaned, id: cleaned.id, body: cleaned.body } : m
            );
          } else if (isInboundDirection(cleaned.direction) || isSameConversationId(cleaned.conversation_id, conversationId)) {
            next = [...prev, cleaned as ChatMessage].sort((a, b) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
          } else { next = prev; }
          if (convIdRef.current) messagesCache.current.set(convIdRef.current, next);
          return next;
        });
      } else {
        messagesCache.current.delete(conversationId);
      }
    };

    const handleWSMessageStatusUpdated = (event: CustomEvent<{ message: WSMessage; conversationId: string }>) => {
      const { message } = event.detail;
      const conversationId = getRealtimeConversationId(message as any, event.detail.conversationId);
      if (isSameConversationId(convIdRef.current, conversationId)) {
        setMessages(prev => {
          const updated = prev.map(m =>
            m.id === message.id || (m.id.startsWith("temp-") && m.body === message.body)
              ? { ...m, status: message.status } : m
          );
          if (convIdRef.current) messagesCache.current.set(convIdRef.current, updated);
          return updated;
        });
      }
    };

    const handleWSConversationUpdated = (event: CustomEvent<{ conversation: any }>) => {
      const convId = event.detail.conversation?.id;
      if (convId && !isSameConversationId(convIdRef.current, convId)) {
        messagesCache.current.delete(convId);
      }
    };

    window.addEventListener("ws-message-created" as any, handleWSMessageCreated);
    window.addEventListener("ws-message-status-updated" as any, handleWSMessageStatusUpdated);
    window.addEventListener("ws-conversation-updated" as any, handleWSConversationUpdated);
    return () => {
      window.removeEventListener("ws-message-created" as any, handleWSMessageCreated);
      window.removeEventListener("ws-message-status-updated" as any, handleWSMessageStatusUpdated);
      window.removeEventListener("ws-conversation-updated" as any, handleWSConversationUpdated);
    };
  }, []);


  // ── Send message ────────────────────────────────────────────────────────────
  const sendMessage = async (): Promise<void> => {
    if (sending) return;
    if (pendingAttachment) {
      await sendAttachmentMessage();
      return;
    }
    const isTemplateMessage = !!selectedTemplate;
    const isRcsTemplateMessage = !!selectedRcsTemplate;
    const messageBody = isTemplateMessage
      ? (templateDraft.trim() || renderTemplateWithVariableValues(selectedTemplate!))
      : isRcsTemplateMessage
        ? getRcsTemplatePreviewText(selectedRcsTemplate!)
        : input.trim();
    if (!messageBody) return;

    setSending(true);
    let currentConversation = conv;

    if (!currentConversation) {
      // New conversation flow — recipient was set in newConvRecipient
      const recipient = newConvRecipient.trim();
      if (!recipient) { setSending(false); return; }
      try {
        const [newConvResponse] = await createConversationRequest({
          title: recipient,
          external_thread_id: recipient,
        });
        if (!newConvResponse?.id) throw new Error("Failed to create conversation");
        currentConversation = newConvResponse;
        setCurrentConversationId(newConvResponse.id);
        onConversationUpdate?.(newConvResponse);
      } catch (error) {
        console.error("Error creating conversation:", error);
        setSending(false);
        return;
      }
    }

    if (!currentConversation) { setSending(false); return; }

    const tempId = `temp-${Date.now()}`;
    const optimistic: ChatMessage = {
      id: tempId,
      tenant_id: currentConversation.tenant_id || "",
      conversation_id: currentConversation.id,
      whatsapp_account_id: currentConversation.whatsapp_account_id || "",
      file_id: null,
      direction: "outbound",
      recipient: currentConversation.external_thread_id,
      body: messageBody,
      type: (isTemplateMessage || isRcsTemplateMessage) ? "template" : "text",
      external_message_id: tempId,
      status: "sending",
      sent_at: new Date().toISOString(),
      delivered_at: null,
      read_at: null,
      created_at: new Date().toISOString(),
      local_pending: true,
    } as ChatMessage;

    setMessages(prev => {
      const next = [...prev, optimistic];
      if (currentConversation?.id) messagesCache.current.set(currentConversation.id, next);
      return next;
    });

    // RCS: never uses the WhatsApp template endpoint — its templates go
    // through the direct send endpoint (type: "template", provider_payload).
    // WhatsApp: use template if selected, otherwise plain text.
    const isRcs = !!(currentConversation.rcs_account_id) || activeChannel === 4;
    const hasRcsTemplate = isRcs && !!selectedRcsTemplate;

    // Widget: routing uses the stored conversation; `recipient` is still
    // required by /messages/send, so it's the literal "visitor".
    const payload = isWidgetConversation ? {
      channel: "widget",
      conversation_id: currentConversation.id,
      recipient: "visitor",
      type: "text",
      body: messageBody,
      idempotency_key: generateIdempotencyKey(),
    } : (!isRcs && isTemplateMessage && selectedTemplate) ? {
      conversation_id: currentConversation.id,
      whatsapp_account_id: currentConversation.whatsapp_account_id,
      recipient: currentConversation.external_thread_id,
      template_name: selectedTemplate.name,
      language: selectedTemplate.language || "en",
      idempotency_key: generateIdempotencyKey(),
      template_attributes: {
        body: templateVarValues.length
          ? templateVarValues
          : getTemplateBodyValues(selectedTemplate, templateDraft),
      },
    } : hasRcsTemplate ? {
      conversation_id: currentConversation.id,
      recipient: currentConversation.external_thread_id,
      type: "template",
      channel: "rcs",
      provider_payload: {
        template_id: selectedRcsTemplate!.id,
        content_variables: rcsContentVariables,
      },
      idempotency_key: generateIdempotencyKey(),
    } : {
      conversation_id: currentConversation.id,
      recipient: currentConversation.external_thread_id,
      body: messageBody,
      type: "text",
      idempotency_key: generateIdempotencyKey(),
      ...(isRcs && { channel: "rcs" }),
    };

    try {
      // RCS: never use template endpoint, always use plain send
      const [response] = (!isRcs && !isWidgetConversation && isTemplateMessage)
        ? await sendTemplateMessageRequest(payload)
        : await sendMessageRequest(payload);

      if (response) {
        const cleanBody = (isTemplateMessage || isRcsTemplateMessage) ? messageBody : (response.body || messageBody);
        setMessages(prev => {
          const updated = settleOptimistic(prev, tempId, { ...response, body: cleanBody, status: "sent" });
          if (currentConversation?.id) messagesCache.current.set(currentConversation.id, updated);
          return updated;
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => prev.map(msg => msg.id === tempId ? { ...msg, status: "error" } : msg));
    } finally {
      setSending(false);
      setInput("");
      setTemplateDraft("");
      setSelectedTemplate(null);
      setTemplateVarValues([]);
      setShowTemplatePicker(false);
      clearRcsTemplate();
      // Reset new conv state
      setNewConvRecipient("");
      setNewConvStep("recipient");
    }
  };

  // ── File attachments (image/video/audio/document) ─────────────────────────
  // Picking a file previews it in the composer (with an optional caption)
  // instead of sending right away — upload happens in the background so it's
  // usually already done by the time the user hits Send.
  // Per conversation-analytics-export-and-media-api.md: webm/csv/plain-text
  // aren't transcoded — they're delivered to WhatsApp as a downloadable
  // document, not a native audio/media bubble. That's true for a recorded
  // voice note too (Chrome's MediaRecorder only produces audio/webm), so
  // both must be tagged "document" rather than "audio" to match what
  // actually reaches the recipient.
  const resolveMediaType = (file: File): MediaType => {
    // MIME comparison is case-insensitive and ignores parameters
    // (e.g. "Audio/WebM; codecs=opus" -> "audio/webm").
    const mimeType = file.type.toLowerCase().split(";")[0].trim();
    if (mimeType === "audio/webm" || mimeType === "text/csv" || mimeType === "text/plain") return "document";
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType.startsWith("video/")) return "video";
    if (mimeType.startsWith("audio/")) return "audio";
    return "document";
  };

  const uploadPendingAttachment = async (file: File, localUrl: string): Promise<void> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const [uploadResponse, uploadStatus] = await uploadFile(formData);
      const fileId = uploadResponse?.file_id;

      if (uploadStatus < 200 || uploadStatus >= 300 || uploadResponse?.status !== "ready" || !fileId) {
        throw new Error("Upload failed");
      }

      setPendingAttachment(prev =>
        prev && prev.localUrl === localUrl ? { ...prev, uploading: false, fileId } : prev
      );
    } catch (error) {
      console.error("Error uploading file:", error);
      setPendingAttachment(prev =>
        prev && prev.localUrl === localUrl ? { ...prev, uploading: false, error: true } : prev
      );
      toast.error(`Failed to upload ${file.name}`);
    }
  };

  const startAttachmentPreview = (file: File): void => {
    if (pendingAttachment) URL.revokeObjectURL(pendingAttachment.localUrl);
    const localUrl = URL.createObjectURL(file);
    setPendingAttachment({ file, localUrl, mediaType: resolveMediaType(file), uploading: true });
    uploadPendingAttachment(file, localUrl);
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    // Snapshot into a real array first — `e.target.files` is a live FileList
    // tied to the input, and resetting `e.target.value` below empties it
    // in place, so reading it after that clear would silently see 0 files.
    const file = Array.from(e.target.files || [])[0];
    e.target.value = ""; // allow re-selecting the same file afterwards
    if (!file) return;
    startAttachmentPreview(file);
  };

  const cancelPendingAttachment = (): void => {
    if (pendingAttachment) URL.revokeObjectURL(pendingAttachment.localUrl);
    setPendingAttachment(null);
  };

  // ── Voice notes ────────────────────────────────────────────────────────────
  // Records via MediaRecorder, then feeds the result through the same
  // preview-before-send flow as a picked file attachment.
  const stopRecordingTracks = (): void => {
    recordingStreamRef.current?.getTracks().forEach(track => track.stop());
    recordingStreamRef.current = null;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const startRecording = async (): Promise<void> => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice recording isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      recordedChunksRef.current = [];

      // Prefer ogg/opus (the common voice-note format) but fall back to
      // whatever this browser can actually record.
      const candidates = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mimeType = candidates.find(type => MediaRecorder.isTypeSupported(type));

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || "audio/webm";
        const blob = new Blob(recordedChunksRef.current, { type: blobType });
        const ext = blobType.includes("ogg") ? "ogg" : blobType.includes("mp4") ? "m4a" : "webm";
        const file = new File([blob], `voice-note-${Date.now()}.${ext}`, { type: blobType });
        stopRecordingTracks();
        startAttachmentPreview(file);
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    } catch (error) {
      console.error("Error starting voice recording:", error);
      toast.error("Couldn't access the microphone.");
    }
  };

  const stopRecording = (): void => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  useEffect(() => () => stopRecordingTracks(), []);

  const sendAttachmentMessage = async (): Promise<void> => {
    if (!conv || !pendingAttachment) return;
    if (pendingAttachment.uploading) {
      toast.info("Still uploading, please wait…");
      return;
    }
    if (pendingAttachment.error || !pendingAttachment.fileId) {
      toast.error("Upload failed — remove the attachment and try again.");
      return;
    }

    const attachment = pendingAttachment;
    // Per rcs.md: only image/video may carry a caption in a direct send —
    // audio and document (PDF) sends reject a non-empty body outright.
    const captionAllowed = attachment.mediaType === "image" || attachment.mediaType === "video";
    const caption = captionAllowed ? input.trim() : "";
    setSending(true);

    const tempId = `temp-${Date.now()}`;
    const optimistic: ChatMessage = {
      id: tempId,
      tenant_id: conv.tenant_id || "",
      conversation_id: conv.id,
      whatsapp_account_id: conv.whatsapp_account_id || "",
      file_id: attachment.fileId || null,
      // mime_type lets our own bubble render audio inline even when `type`
      // is "document" (webm voice notes are dispatched as documents per
      // conversation-analytics-export-and-media-api.md, but we still have
      // the real file and can let the agent play it here).
      file: { url: attachment.localUrl, name: attachment.file.name, mime_type: attachment.file.type },
      // Survive the post-send `{ ...msg, ...response }` merge even when the
      // response's `file` is null/incomplete or doesn't echo mime_type back
      // (both observed) — see ChatMessage._localPreview/_mimeType.
      _localPreview: attachment.localUrl,
      _mimeType: attachment.file.type,
      direction: "outbound",
      recipient: conv.external_thread_id,
      body: caption,
      type: attachment.mediaType,
      external_message_id: tempId,
      status: "sending",
      sent_at: new Date().toISOString(),
      delivered_at: null,
      read_at: null,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => {
      const next = [...prev, optimistic];
      messagesCache.current.set(conv.id, next);
      return next;
    });
    setPendingAttachment(null);
    setInput("");

    try {
      const payload = {
        conversation_id: conv.id,
        recipient: conv.external_thread_id,
        type: attachment.mediaType,
        file_id: attachment.fileId,
        body: caption,
        idempotency_key: generateIdempotencyKey(),
        ...(isRcsConversation && { channel: "rcs" }),
      };
      const [response] = await sendMessageRequest(payload);

      setMessages(prev => {
        const updated = settleOptimistic(prev, tempId, { ...response, status: "sent" });
        messagesCache.current.set(conv.id, updated);
        return updated;
      });
    } catch (error) {
      console.error("Error sending attachment:", error);
      toast.error("Failed to send attachment");
      setMessages(prev => {
        const updated = prev.map(msg => msg.id === tempId ? { ...msg, status: "failed" as const } : msg);
        messagesCache.current.set(conv.id, updated);
        return updated;
      });
    } finally {
      setSending(false);
    }
  };

  const getPrefillData = () => {
    if (existingTicket?.customer_name) return {
      name: existingTicket.customer_name || "",
      phone: existingTicket.customer_phone || "",
      email: existingTicket.customer_email || "",
      location: existingTicket?.customer_city || "",
    };
    if (leadContact) return {
      name: getContactDisplayName(leadContact),
      phone: leadContact.phone || "",
      email: leadContact.email || "",
      location: leadContact?.custom?.city || "",
    };
    return undefined;
  };

  // ── Template selection → open the variable-configuration popup ─────────────
  // Used by TemplatePickerModal's onSelect (both the "new conversation" flow
  // and the "active conversation" flow below). Instead of immediately
  // finalizing the template, we stage it and let the user fill in {{n}}
  // variables in TemplateVariableModal first.
  const handleTemplatePicked = (template: ExtendedTemplate) => {
    setPendingTemplate(template);
    setShowVariableModal(true);
  };

  // ── Variable modal confirmed → finalize the template selection ─────────────
  const handleVariablesConfirmed = (values: string[], renderedText: string) => {
    setSelectedTemplate(pendingTemplate);
    setTemplateVarValues(values);
    setTemplateDraft(renderedText);
    setInput("");
    setPendingTemplate(null);
  };

  // ── RCS Template Handlers ──────────────────────────────────────────────────
  // Helper to extract variables from RCS template components.
  // Supports both numeric {{1}} and named {{first_name}} variables.
  const extractRcsVariableIndices = (components: RcsComponent[]): string[] => {
    const variables = new Set<string>();
    components.forEach((component) => {
      const c = component as any;
      const checkContent = (content?: string) => {
        if (!content) return;
        const matches = content.matchAll(/{{(\w+)}}/g);
        for (const match of matches) variables.add(match[1]);
      };
      // API uses 'text', types use 'content'
      if (component.type === "text") checkContent(c.content || c.text);
      if (component.type === "rich_card") {
        checkContent(component.title);
        checkContent(component.description);
      }
      if (component.type === "carousel" && component.cards) {
        component.cards.forEach((card) => {
          checkContent(card.title);
          checkContent(card.description);
        });
      }
    });
    // Sort: numbers first (numerically), then strings alphabetically
    return [...variables].sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      if (!isNaN(aNum)) return -1;
      if (!isNaN(bNum)) return 1;
      return a.localeCompare(b);
    });
  };

  const handleRcsTemplatePicked = (template: RcsTemplate) => {
    const variableIndices = extractRcsVariableIndices(template.components || []);
    if (variableIndices.length === 0) {
      // No variables — skip the modal and select directly.
      setSelectedRcsTemplate(template);
      setRcsContentVariables({});
      setInput("");
    } else {
      setPendingRcsTemplate(template);
      setShowRcsVariableModal(true);
    }
  };

  const handleRcsVariablesConfirmed = (contentVariables: Record<string, string>, _previewComponents: RcsComponent[]) => {
    setSelectedRcsTemplate(pendingRcsTemplate);
    setRcsContentVariables(contentVariables);
    setPendingRcsTemplate(null);
    setInput("");
  };

  const clearRcsTemplate = () => {
    setSelectedRcsTemplate(null);
    setRcsContentVariables({});
    setPendingRcsTemplate(null);
  };

  // Text preview used both as the optimistic message body and disable-guard.
  const getRcsTemplatePreviewText = (template: RcsTemplate): string => {
    if (template.content) return template.content;
    for (const comp of template.components || []) {
      const c = comp as any;
      const textContent = c.content || c.text;
      if (comp.type === "text" && textContent) {
        let text = textContent;
        Object.entries(rcsContentVariables).forEach(([idx, val]) => {
          text = text.replace(new RegExp(`\\{\\{${idx}\\}\\}`, "g"), val || `{{${idx}}}`);
        });
        return text;
      }
      if (comp.type === "rich_card") {
        const title = comp.title || "";
        const desc = comp.description || "";
        let text = `${title}${desc ? "\n" + desc : ""}`.trim();
        Object.entries(rcsContentVariables).forEach(([idx, val]) => {
          text = text.replace(new RegExp(`\\{\\{${idx}\\}\\}`, "g"), val || `{{${idx}}}`);
        });
        return text || template.template_name;
      }
    }
    return template.template_name;
  };

  // ── Quick reply picked → drop it into the composer for review, don't auto-send ──
  const handleQuickReplySelected = (reply: QuickReply) => {
    setSelectedTemplate(null);
    setTemplateDraft("");
    setTemplateVarValues([]);
    setInput(reply.message);
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const dateKey = formatDate(msg.created_at);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
    return groups;
  }, {} as Record<string, ChatMessage[]>);

  // ── NEW CONVERSATION state (no conv selected) ──────────────────────────────
  if (!conv) {
    // RCS: simple flow - just recipient + message (no templates)
    // WhatsApp: requires template for new conversations
    if (isRcsConversation) {
      return (
        <div className="flex-1 flex flex-col bg-[#f0f2f5] dark:bg-gray-950 min-w-0">
          {/* Header */}
          <div className="bg-white dark:bg-gray-900 px-4 py-3 flex items-center gap-3 border-b border-[#e8ecf4] dark:border-gray-800 shadow-sm">
            <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-500/15 flex items-center justify-center text-purple-500 dark:text-purple-400">
              <Icons.RCS size={18} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm text-slate-800 dark:text-gray-100">New RCS Conversation</div>
              <div className="text-xs text-slate-400 dark:text-gray-500">Start a new RCS chat</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="text-center text-slate-400 dark:text-gray-500 text-sm">
              Enter a recipient and message to start the conversation.
            </div>
          </div>

          {/* Input area */}
          <div className="bg-white dark:bg-gray-900 px-4 py-3 border-t border-[#e8ecf4] dark:border-gray-800">
            <div className="mb-3">
              <input
                value={newConvRecipient}
                onChange={e => setNewConvRecipient(e.target.value)}
                placeholder="Enter phone number (e.g. +2348012345678)"
                className="w-full border border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 dark:focus:ring-indigo-500/30"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !sending && newConvRecipient.trim() && input.trim() && sendMessage()}
                placeholder="Write your message..."
                disabled={sending}
                className="flex-1 min-w-0 border border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 dark:focus:ring-indigo-500/30 disabled:opacity-50"
              />
              <button
                onClick={sendMessage}
                disabled={!newConvRecipient.trim() || !input.trim() || sending}
                className="shrink-0 bg-indigo-500 text-white border-none rounded-lg px-4 py-2 font-bold text-sm cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Icons.Send />
                )}
                <span className="hidden sm:inline">Send</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    // WhatsApp: template-based new conversation flow
    return (
      <>
        <div className="flex-1 flex flex-col bg-[#f0f2f5] dark:bg-gray-950 min-w-0">
          {/* Header */}
          <div className="bg-white dark:bg-gray-900 px-4 py-3 flex items-center gap-3 border-b border-[#e8ecf4] dark:border-gray-800 shadow-sm">
            <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center text-indigo-500 dark:text-indigo-400">
              <Icons.Plus size={18} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm text-slate-800 dark:text-gray-100">New Conversation</div>
              <div className="text-xs text-slate-400 dark:text-gray-500">Start a fresh chat via WhatsApp</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col gap-4 p-4">
            {/* Step 1: Recipient */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">1</div>
                <p className="text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase tracking-wide">Recipient</p>
              </div>
              <div className="flex gap-2">
                <input
                  value={newConvRecipient}
                  onChange={e => setNewConvRecipient(e.target.value)}
                  placeholder="Enter phone number (e.g. +2348012345678)"
                  className="flex-1 border border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 dark:focus:ring-indigo-500/30"
                />
              </div>
              <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-1.5">Include country code, e.g. +234 for Nigeria</p>
            </div>

            {/* Step 2: Template — only shown after recipient is entered */}
            {newConvRecipient.trim() && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">2</div>
                  <p className="text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase tracking-wide">Select Template</p>
                </div>

                {selectedTemplate ? (
                  <div>
                    <div className="flex items-start gap-3 p-3 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/30 rounded-lg">
                      {(selectedTemplate as any).snapshot_url ? (
                        <img
                          src={(selectedTemplate as any).snapshot_url}
                          alt={selectedTemplate.name}
                          className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-indigo-200 dark:border-indigo-500/30"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                          <Icons.Whatsapp />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm text-indigo-700 dark:text-indigo-300 truncate">{selectedTemplate.name}</span>
                          <button
                            onClick={() => {
                              setSelectedTemplate(null);
                              setTemplateVarValues([]);
                              setTemplateDraft("");
                            }}
                            className="text-slate-400 dark:text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
                          >
                            {/* <Icons.Times size={13} /> */}
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
                          {templateDraft || selectedTemplate.preview}
                        </p>
                        <div className="flex gap-1.5 mt-1.5">
                          <span className="text-[10px] bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-gray-300 px-2 py-px rounded-full">
                            {selectedTemplate.category}
                          </span>
                          <span className="text-[10px] bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 px-2 py-px rounded-full font-semibold">
                            {selectedTemplate.wa_status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => setShowTemplatePicker(true)}
                        className="flex-1 text-xs text-indigo-600 dark:text-indigo-400 font-semibold py-1.5 hover:underline"
                      >
                        Change template
                      </button>
                      <button
                        onClick={() => {
                          setPendingTemplate(selectedTemplate);
                          setShowVariableModal(true);
                        }}
                        className="flex-1 text-xs text-indigo-600 dark:text-indigo-400 font-semibold py-1.5 hover:underline"
                      >
                        Edit variables
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowTemplatePicker(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-indigo-200 dark:border-indigo-500/30 rounded-lg text-sm font-semibold text-indigo-500 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-colors"
                  >
                    <Icons.Plus size={14} />
                    Choose a template
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Send row */}
          <div className="bg-white dark:bg-gray-900 px-4 py-3 border-t border-[#e8ecf4] dark:border-gray-800">
            {(!newConvRecipient.trim() || !selectedTemplate) && (
              <div className="flex gap-2 mb-2.5">
                {!newConvRecipient.trim() && (
                  <span className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 rounded-full px-2.5 py-0.5">
                    Enter recipient first
                  </span>
                )}
                {newConvRecipient.trim() && !selectedTemplate && (
                  <span className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 rounded-full px-2.5 py-0.5">
                    Select a template
                  </span>
                )}
              </div>
            )}
            <button
              onClick={sendMessage}
              disabled={!newConvRecipient.trim() || !selectedTemplate || sending}
              className="w-full bg-indigo-500 text-white rounded-xl py-2.5 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
            >
              {sending ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Sending...</>
              ) : (
                <><Icons.Send /> {selectedTemplate ? `Send "${selectedTemplate.name}"` : "Send Template"}</>
              )}
            </button>
          </div>
        </div>

        {/* Template Picker Modal */}
        <TemplatePickerModal
          isOpen={showTemplatePicker}
          onClose={() => setShowTemplatePicker(false)}
          templates={backendTemplates}
          onSelect={handleTemplatePicked}
        />

        {/* Template Variable Configuration Modal */}
        <TemplateVariableModal
          isOpen={showVariableModal}
          onClose={() => {
            setShowVariableModal(false);
            setPendingTemplate(null);
          }}
          template={pendingTemplate}
          onConfirm={handleVariablesConfirmed}
        />
      </>
    );
  }

  // ── Active conversation ────────────────────────────────────────────────────
  const initials = getInitials(conv.title);
  const color = getColorFromId(conv.id);
  const statusInfo = getStatusBadge(conv.status);

  // Detect if 24-hour window is closed (conv.status === "closed" or a flag from backend)
  // RCS does NOT use 24-hour template window - always allow free-form messages
  const isWindowClosed = !isRcsConversation && !isWidgetConversation && (
    (conv as any).window_closed === true ||
    (conv.status === "closed") ||
    ((conv as any).is_outside_window === true)
  );

  return (
    <>
      <div className="flex-1 flex flex-col bg-[#f0f2f5] dark:bg-gray-950 min-w-0">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 px-4 py-3 flex items-center gap-3 border-b border-[#e8ecf4] dark:border-gray-800 shadow-sm">
          <Avatar src={null} initials={initials} color={color} />
          <div className="flex-1">
            <div className="font-bold text-sm text-slate-800 dark:text-gray-100">{conv.title || conv.external_thread_id}</div>
            <div className="flex gap-1.5 mt-0.5">
              <span className={`text-[11px] font-semibold px-[7px] py-px rounded-full ${statusInfo.className}`}>
                {statusInfo.label}
              </span>
              <span className="text-[11px] font-semibold px-[7px] py-px rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                ID: {conv.external_thread_id}
              </span>
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-[7px] py-px rounded-full ${
                  manualReplyEnabled
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                    : "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                }`}
              >
                {manualReplyEnabled ? <Headset size={11} /> : <Bot size={11} />}
                {manualReplyEnabled ? "Human" : "Bot"}
              </span>
            </div>
          </div>
          <div className="flex gap-2 text-slate-500 dark:text-gray-400">
            <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center justify-center">
              <Icons.Phone />
            </button>
            <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center justify-center">
              <Icons.Search />
            </button>
            {isMobile && (
              <button
                className="bg-indigo-500 text-white rounded-lg px-2.5 py-1.5 text-xs font-bold cursor-pointer"
                onClick={onOpenInsights}
              >
                Insights
              </button>
            )}
            <div className="relative inline-block" ref={actionMenuRef}>
              <button
                onClick={() => setOpenAction(prev => !prev)}
                className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center justify-center"
              >
                <Icons.MoreVert />
              </button>
              {openAction && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 overflow-hidden">
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => { setOpenAction(false); setOpenUpdateModal(true); }}
                  >
                    <Contact className="w-4 h-4" /> <span>Update Contact</span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => { setOpenAction(false); setIsOpen(true); }}
                  >
                    <Ticket className="w-4 h-4" /> <span>Assign Tickets</span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleArchiveConversation}
                    disabled={archiving}
                  >
                    <Archive className="w-4 h-4" /> <span>{archiving ? "Archiving…" : "Archive"}</span>
                  </button>

                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => { setOpenAction(false); setShowAssignAgentModal(true); }}
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>{assignedAgent ? `Assigned: ${assignedAgent.first_name} ${assignedAgent.last_name}` : "Assign Agent"}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto pb-2">
          {messages.length === 0 ? (
            <div className="text-center text-slate-400 dark:text-gray-500 text-sm py-8">
              No messages yet. Send a message to start the conversation!
            </div>
          ) : (
            Object.entries(groupedMessages).map(([date, dateMessages]) => (
              <div key={date}>
                <div className="text-center pt-3 pb-1.5">
                  <span className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-full text-xs text-slate-500 dark:text-gray-400 px-3.5 py-[3px]">
                    {date}
                  </span>
                </div>
                {dateMessages.map(msg => <MessageComponent key={msg.id} msg={msg} onOpenForm={onOpenForm} templates={templateCatalog} />)}
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* ── 24-hour window closed banner OR normal input ──
            Once a template is picked, drop the banner so the composer (in
            template mode) shows and the user can actually send it — the
            banner otherwise never gets out of the way. */}
        {isWindowClosed && !selectedTemplate ? (
          <WindowClosedBanner onSelectTemplate={() => setShowTemplatePicker(true)} />
        ) : (
          <div className="bg-white dark:bg-gray-900 px-3.5 py-2.5 border-t border-[#e8ecf4] dark:border-gray-800">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <button
                onClick={() => setShowQuickReplyModal(true)}
                className="shrink-0 bg-slate-100 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 px-3 py-1 text-xs text-indigo-500 dark:text-indigo-400 font-semibold cursor-pointer flex items-center gap-1"
              >
                <Reply size={14} /> <span className="hidden sm:inline">Quick Replies</span>
              </button>
              {/* Hide template button for RCS/widget - neither uses WhatsApp templates */}
              {!isRcsConversation && !isWidgetConversation && (
                <>
                  <button
                    onClick={() => {
                      if (selectedTemplate) {
                        setSelectedTemplate(null);
                        setTemplateDraft("");
                        setTemplateVarValues([]);
                        setInput("");
                        return;
                      }
                      setShowTemplatePicker(true);
                    }}
                    disabled={!!pendingAttachment}
                    className={`shrink-0 rounded-lg border px-3 py-1 text-xs font-semibold cursor-pointer flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${selectedTemplate
                      ? "bg-indigo-50 border-indigo-200 text-indigo-600 dark:bg-indigo-500/15 dark:border-indigo-500/30 dark:text-indigo-400"
                      : "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"}`}
                  >
                    <Icons.Comment /> <span className="hidden sm:inline">{selectedTemplate ? "Text mode" : "Templates"}</span>
                  </button>
                  {selectedTemplate && (
                    <button
                      onClick={() => {
                        setPendingTemplate(selectedTemplate);
                        setShowVariableModal(true);
                      }}
                      className="shrink-0 rounded-lg border px-3 py-1 text-xs font-semibold cursor-pointer flex items-center gap-1 transition-colors bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <span className="sm:hidden">Vars</span>
                      <span className="hidden sm:inline">Edit variables</span>
                    </button>
                  )}
                </>
              )}
              {/* RCS: separate template catalog from WhatsApp's */}
              {isRcsConversation && (
                <>
                  <button
                    onClick={() => {
                      if (selectedRcsTemplate) {
                        clearRcsTemplate();
                        return;
                      }
                      setShowRcsTemplatePicker(true);
                    }}
                    disabled={!!pendingAttachment}
                    className={`shrink-0 rounded-lg border px-3 py-1 text-xs font-semibold cursor-pointer flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${selectedRcsTemplate
                      ? "bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-500/15 dark:border-purple-500/30 dark:text-purple-400"
                      : "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"}`}
                  >
                    <Icons.RCS size={14} /> <span className="hidden sm:inline">{selectedRcsTemplate ? "Text mode" : "RCS Templates"}</span>
                  </button>
                  {selectedRcsTemplate && (
                    <button
                      onClick={() => {
                        setPendingRcsTemplate(selectedRcsTemplate);
                        setShowRcsVariableModal(true);
                      }}
                      className="shrink-0 rounded-lg border px-3 py-1 text-xs font-semibold cursor-pointer flex items-center gap-1 transition-colors bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <span className="sm:hidden">Vars</span>
                      <span className="hidden sm:inline">Edit variables</span>
                    </button>
                  )}
                </>
              )}
              <div className="flex-1 min-w-[200px] flex flex-col bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-1.5 gap-1.5">
                {pendingAttachment && (
                  <div className="flex items-center gap-2 pb-1.5 border-b border-slate-200 dark:border-gray-700">
                    {pendingAttachment.mediaType === "image" ? (
                      <img
                        src={pendingAttachment.localUrl}
                        alt="Attachment preview"
                        className="w-10 h-10 rounded-md object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-slate-200 dark:bg-gray-700 flex items-center justify-center shrink-0 text-slate-500 dark:text-gray-400">
                        <Icons.Attach />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 dark:text-gray-200 truncate">{pendingAttachment.file.name}</p>
                      <p className="text-[11px] text-slate-400 dark:text-gray-500">
                        {pendingAttachment.error
                          ? "Upload failed"
                          : pendingAttachment.uploading
                            ? "Uploading…"
                            : "Ready to send"}
                      </p>
                    </div>
                    {pendingAttachment.uploading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent shrink-0" />
                    )}
                    <button
                      type="button"
                      onClick={cancelPendingAttachment}
                      title="Remove attachment"
                      className="bg-transparent border-none cursor-pointer p-1 rounded text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-700 shrink-0"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {isRecording ? (
                    <div className="flex-1 flex items-center gap-2 text-sm text-red-500 font-medium">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                      Recording… {String(Math.floor(recordingSeconds / 60)).padStart(2, "0")}:{String(recordingSeconds % 60).padStart(2, "0")}
                    </div>
                  ) : (
                    <input
                      value={selectedTemplate ? templateDraft : input}
                      onChange={e => selectedTemplate ? setTemplateDraft(e.target.value) : setInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !sending && sendMessage()}
                      placeholder={
                        selectedTemplate
                          ? "Customize template message..."
                          : selectedRcsTemplate
                            ? "RCS template selected — use \"Edit variables\" to customize"
                            : pendingAttachment
                              ? (pendingAttachment.mediaType === "audio" || pendingAttachment.mediaType === "document")
                                ? "Captions aren't supported for this file type"
                                : "Add a caption…"
                              : "Write your message..."
                      }
                      disabled={sending || !!selectedRcsTemplate || (!!pendingAttachment && (pendingAttachment.mediaType === "audio" || pendingAttachment.mediaType === "document"))}
                      className="flex-1 border-none bg-transparent outline-none text-sm text-slate-800 dark:text-gray-100 disabled:opacity-50"
                    />
                  )}
                  {!isWidgetConversation && (
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={!isRecording && (!!selectedTemplate || !!selectedRcsTemplate || !!pendingAttachment)}
                    title={isRecording ? "Stop recording" : "Record a voice note"}
                    className={`border-none cursor-pointer p-1.5 rounded-lg transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed ${
                      isRecording ? "bg-red-500 text-white hover:bg-red-600" : "bg-transparent text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    {isRecording ? <Square size={14} fill="currentColor" /> : <Icons.Mic />}
                  </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    onChange={handleFilesSelected}
                    className="hidden"
                  />
                  {!isWidgetConversation && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!!selectedTemplate || !!selectedRcsTemplate || !!pendingAttachment || isRecording}
                    title="Attach image, video, audio or document"
                    className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icons.Attach />
                  </button>
                  )}
                </div>
              </div>
              <button
                onClick={sendMessage}
                disabled={(!input.trim() && !selectedTemplate && !selectedRcsTemplate && !pendingAttachment) || sending || pendingAttachment?.uploading}
                className="shrink-0 bg-indigo-500 text-white border-none rounded-lg px-4 py-2 font-bold text-sm cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending
                  ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  : <Icons.Send />}
                <span className="hidden sm:inline">{selectedTemplate || selectedRcsTemplate ? "Send Template" : "Send"}</span>
              </button>
            </div>
            {isRcsConversation && selectedRcsTemplate && (
              <div className="mb-2 p-2.5 bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-500/15 flex items-center justify-center text-purple-600 dark:text-purple-400 flex-shrink-0">
                    <Icons.RCS size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-purple-700 dark:text-purple-300 truncate">
                      {selectedRcsTemplate.template_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400 rounded-full">
                        {selectedRcsTemplate.category}
                      </span>
                      <span className="text-[10px] text-purple-500 dark:text-purple-400">
                        {selectedRcsTemplate.components?.length || 0} component{(selectedRcsTemplate.components?.length || 0) !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={clearRcsTemplate}
                    className="p-1.5 rounded-lg text-slate-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0 bg-transparent border-none cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div
                className={`flex items-center gap-2 rounded-full border pl-2.5 pr-1 py-1 ${
                  manualReplyEnabled
                    ? "border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10"
                    : "border-indigo-100 bg-indigo-50/70 dark:border-indigo-500/30 dark:bg-indigo-500/10"
                }`}
              >
                <span
                  className={`flex items-center gap-1.5 text-xs font-semibold ${
                    manualReplyEnabled ? "text-amber-800 dark:text-amber-300" : "text-indigo-700 dark:text-indigo-300"
                  }`}
                >
                  {manualReplyEnabled ? <Headset size={14} /> : <Bot size={14} />}
                  {manualReplyEnabled ? "You're replying · bot paused" : "Bot is replying"}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={manualReplyEnabled}
                  aria-label="Manual reply"
                  onClick={handleToggleManualReply}
                  disabled={takeoverLoading}
                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold border cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    manualReplyEnabled
                      ? "bg-white dark:bg-gray-900 border-amber-300 dark:border-amber-500/40 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                      : "bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700"
                  }`}
                >
                  {takeoverLoading ? "…" : manualReplyEnabled ? "Hand back to bot" : "Take over"}
                </button>
              </div>
              <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-1.5 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-1 cursor-pointer text-xs font-semibold text-slate-600 dark:text-gray-300"
              >
                <Icons.Agents size={14} />
                {existingTicket?.assigned_to_user_id ? `Assigned: ${getAssignedTo(existingTicket, team)}` : "Assign To"}
                <Icons.ChevronDown size={13} />
              </button>
            </div>
          </div>
        )}

        <RaiseTicketModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          conversationId={currentConversationId || conv.id}
          prefillCustomerData={getPrefillData()}
          existingTicket={existingTicket}
          onSaved={ticket => { setExistingTicket(ticket); onTicketSaved?.(ticket); }}
        />
        <ContactModal
          isOpen={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
          conversationId={currentConversationId || conv.id}
          contact={leadContact}
          fallbackName={conv.title}
          onUpdated={onContactUpdated}
        />
        <AssignConversationAgentModal
          isOpen={showAssignAgentModal}
          conversationTitle={getContactDisplayName(leadContact) !== "N.A." ? getContactDisplayName(leadContact) : conv.title}
          teamMembers={team}
          currentAssignedId={assignedAgentId}
          onClose={() => setShowAssignAgentModal(false)}
          onAssign={handleAssignConversationAgent}
        />
      </div>

      {/* Template Picker Modal — for existing conversation */}
      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        templates={backendTemplates}
        onSelect={handleTemplatePicked}
      />

      {/* Template Variable Configuration Modal — for existing conversation */}
      <TemplateVariableModal
        isOpen={showVariableModal}
        onClose={() => {
          setShowVariableModal(false);
          setPendingTemplate(null);
        }}
        template={pendingTemplate}
        onConfirm={handleVariablesConfirmed}
      />

      <SendQuickReplyModal
        isOpen={showQuickReplyModal}
        onClose={() => setShowQuickReplyModal(false)}
        recipientName={conv.title || conv.external_thread_id}
        onSelect={handleQuickReplySelected}
      />

      {/* RCS Template Picker Modal */}
      <RcsTemplatePickerModal
        isOpen={showRcsTemplatePicker}
        onClose={() => setShowRcsTemplatePicker(false)}
        onSelect={handleRcsTemplatePicked}
      />

      {/* RCS Template Variable Configuration Modal */}
      <RcsTemplateVariableModal
        isOpen={showRcsVariableModal}
        onClose={() => {
          setShowRcsVariableModal(false);
          setPendingRcsTemplate(null);
        }}
        template={pendingRcsTemplate}
        onConfirm={handleRcsVariablesConfirmed}
      />
    </>
  );
};